/**
 * Before / after image comparison.
 *
 * Rewritten from the stock component to fix three things:
 *  1. Pressing the handle used to add `body-no-scrollbar` to <body>, which set
 *     overflow:hidden + padding-right:15px. The page scrollbar vanished and the
 *     whole layout jumped sideways on mousedown, and on touch it locked the
 *     page scroll. Nothing here touches body scrolling any more.
 *  2. Dragging only started on the handle (2.8rem wide) — unusable with a
 *     thumb. The whole media area is now the drag surface.
 *  3. The position was derived from `offsetLeft` / `pageX`, which is wrong as
 *     soon as the element sits inside a positioned ancestor. It now uses
 *     getBoundingClientRect() + clientX, so the seam lands under the pointer.
 *
 * Vertical page scrolling still works over the image: CSS sets
 * touch-action: pan-y, so a vertical swipe scrolls and cancels the drag.
 */
if (!customElements.get('image-comparison')) {
  customElements.define(
    'image-comparison',
    class ImageComparison extends HTMLElement {
      constructor() {
        super();

        this.button = this.querySelector('button');
        this.isHorizontal = this.dataset.direction !== 'vertical';
        this.percent = 50;
        this.dragging = false;

        this.onPointerDown = this.startDrag.bind(this);
        this.onPointerMove = this.drag.bind(this);
        this.onPointerUp = this.stopDrag.bind(this);
        this.onKeyDown = this.keydown.bind(this);

        this.init();
      }

      init() {
        this.addEventListener('pointerdown', this.onPointerDown);
        this.addEventListener('pointermove', this.onPointerMove);
        ['pointerup', 'pointercancel', 'lostpointercapture'].forEach((type) =>
          this.addEventListener(type, this.onPointerUp)
        );
        this.addEventListener('dragstart', (event) => event.preventDefault());

        if (this.button) {
          this.button.setAttribute('role', 'slider');
          this.button.setAttribute('aria-valuemin', '0');
          this.button.setAttribute('aria-valuemax', '100');
          this.button.setAttribute('aria-valuenow', '50');
          this.button.addEventListener('keydown', this.onKeyDown);
          this.button.addEventListener('click', (event) => event.preventDefault());
        }

        const trigger = this.querySelector('.image-comparison__animation-trigger');
        if (window.FoxTheme && FoxTheme.Motion && trigger) {
          FoxTheme.Motion.inView(trigger, this.animation.bind(this));
        } else {
          this.setAttribute('is-visible', '');
        }
      }

      animation() {
        this.setAttribute('is-visible', '');
        if (window.FoxTheme && FoxTheme.a11y && FoxTheme.a11y.shouldAnimate()) {
          this.classList.add('is-animating');
          setTimeout(() => {
            this.classList.remove('is-animating');
          }, 1e3);
        }
      }

      startDrag(event) {
        if (event.button && event.button !== 0) return;

        this.dragging = true;
        this.classList.add('is-dragging');
        try {
          this.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture is a nicety; dragging still works without it.
        }
        this.move(event);
      }

      drag(event) {
        if (!this.dragging) return;
        event.preventDefault();
        this.move(event);
      }

      stopDrag() {
        if (!this.dragging) return;
        this.dragging = false;
        this.classList.remove('is-dragging');
      }

      move(event) {
        const rect = this.getBoundingClientRect();
        const size = this.isHorizontal ? rect.width : rect.height;
        if (!size) return;

        let percent = this.isHorizontal
          ? ((event.clientX - rect.left) / rect.width) * 100
          : ((event.clientY - rect.top) / rect.height) * 100;

        if (this.isHorizontal && window.FoxTheme && FoxTheme.config && FoxTheme.config.isRTL) {
          percent = 100 - percent;
        }

        this.setPercent(percent, size);
      }

      // Keep the knob fully inside the frame, the way the stock component did.
      setPercent(value, size) {
        const frame = size || (this.isHorizontal ? this.clientWidth : this.clientHeight);
        const knob = this.button && this.button.firstElementChild;
        const inset = knob && knob.offsetWidth ? knob.offsetWidth / 2 : 20;
        const limit = frame ? (inset / frame) * 100 : 0;

        this.percent = Math.max(limit, Math.min(100 - limit, value));
        this.style.setProperty('--percent', this.percent + '%');
        if (this.button) this.button.setAttribute('aria-valuenow', Math.round(this.percent));
      }

      keydown(event) {
        const step = event.shiftKey ? 10 : 5;
        const keys = {
          ArrowLeft: -step,
          ArrowUp: -step,
          ArrowRight: step,
          ArrowDown: step,
        };

        if (event.key in keys) {
          event.preventDefault();
          this.setPercent(this.percent + keys[event.key]);
        } else if (event.key === 'Home') {
          event.preventDefault();
          this.setPercent(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          this.setPercent(100);
        }
      }
    }
  );
}
